import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Youtube, Linkedin, Facebook, Instagram } from 'lucide-react';

/* lucide-react has no TikTok mark, so it ships here as an inline SVG.
   Same props shape as a lucide icon and fills with currentColor, so it
   picks up the hover colour exactly like the others. Rendered slightly
   smaller because a solid glyph reads heavier than a stroked one. */
const TikTok = ({ size = 16 }) => (
  <svg width={size * 0.92} height={size * 0.92} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1 0-5.18c.27 0 .53.04.77.12v-3.2a5.76 5.76 0 0 0-.77-.05 5.78 5.78 0 1 0 5.78 5.78V9.01a7.35 7.35 0 0 0 4.31 1.38V7.3a4.28 4.28 0 0 1-3.35-1.48z" />
  </svg>
);

/* Update the two '#' placeholders once the LinkedIn and Instagram URLs are known. */
const socials = [
  { Icon: Youtube,   label: 'YouTube',   href: 'https://youtube.com/@alteracodeofficial?si=ZqY2T9ehrobZ_bw5' },
  { Icon: Linkedin,  label: 'LinkedIn',  href: '#' },
  { Icon: Facebook,  label: 'Facebook',  href: 'https://www.facebook.com/share/1BeCZxp3sM/' },
  { Icon: Instagram, label: 'Instagram', href: '#' },
  { Icon: TikTok,    label: 'TikTok',    href: 'https://www.tiktok.com/@alteracode.offici?_r=1&_t=ZS-98RcLX0wfjw' },
];

const FooterCol = ({ title, links }) => (
  <div>
    <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--ac-white)', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h4>
    {links.map((l, i) => (
      <Link key={i} to={l.to} style={{
        display: 'block', color: 'var(--ac-muted)', textDecoration: 'none',
        fontSize: 14, marginBottom: 8, transition: 'color 0.2s',
      }}
        onMouseEnter={e => e.target.style.color = 'var(--ac-cyan)'}
        onMouseLeave={e => e.target.style.color = 'var(--ac-muted)'}
      >{l.label}</Link>
    ))}
  </div>
);

export default function Footer() {
  return (
    <footer style={{ background: 'var(--ac-navy)', borderTop: '1px solid var(--ac-border)', paddingTop: '4rem' }}>
      <div className="container">
        <div className="layout-footer" style={{ marginBottom: '3rem' }}>
          
          {/* Brand */}
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <img 
                src="/logo.png" 
                alt="AlteraCode" 
                style={{ 
                  height: 60, 
                  width: 'auto',
                  objectFit: 'contain',
                }} 
              />
            </div>
            <p style={{ fontSize: 13, color: 'var(--ac-muted)', lineHeight: 1.7, marginBottom: '0.75rem', maxWidth: 280 }}>
              A global IT company powered by 13+ skilled developers — delivering innovative technology solutions and bridging the gap from learning to career through Altera Scholar.
            </p>
            <p style={{ fontSize: 12, color: 'var(--ac-muted)', fontStyle: 'italic', marginBottom: '1.5rem' }}>
              Code that transforms.
            </p>

            {/* Founded badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(0,200,255,0.08)', border: '1px solid rgba(0,200,255,0.2)',
              borderRadius: 20, padding: '4px 12px', marginBottom: '1.5rem',
            }}>
              <span style={{ fontSize: 11, color: 'var(--ac-cyan)' }}>🌍 Founded March 2026 · Colombo</span>
            </div>

            {/* Social icons */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {socials.map(({ Icon, label, href }) => (
                <a key={label} href={href} title={label} aria-label={label}
                  target={href === '#' ? undefined : '_blank'}
                  rel={href === '#' ? undefined : 'noopener noreferrer'}
                  style={{
                    width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid var(--ac-border)',
                    color: 'var(--ac-muted)', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', transition: 'var(--transition)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={e => { 
                    e.currentTarget.style.background = 'rgba(0,200,255,0.1)'; 
                    e.currentTarget.style.color = 'var(--ac-cyan)'; 
                    e.currentTarget.style.borderColor = 'rgba(0,200,255,0.3)'; 
                  }}
                  onMouseLeave={e => { 
                    e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; 
                    e.currentTarget.style.color = 'var(--ac-muted)'; 
                    e.currentTarget.style.borderColor = 'var(--ac-border)'; 
                  }}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Company links */}
          <FooterCol title="Company" links={[
            { to: '/', label: 'Home' },
            { to: '/about', label: 'About Us' },
            { to: '/contact', label: 'Contact Us' },
          ]} />

          <FooterCol title="Services" links={[
            { to: '/contact', label: 'Web Development' },
            { to: '/contact', label: 'Mobile App Development' },
            { to: '/contact', label: 'AI & Machine Learning' },
            { to: '/contact', label: 'Data Engineering' },
            { to: '/contact', label: 'UI/UX Design' },
          ]} />

          {/* Altera Scholar links */}
          <FooterCol title="Altera Scholar" links={[
            { to: '/educenter', label: 'All Pathways' },
            { to: '/educenter?track=computer-science', label: 'Computer Science' },
            { to: '/educenter?track=data-science', label: 'Data Science' },
            { to: '/educenter?track=ai-ml', label: 'AI & Machine Learning' },
            { to: '/educenter?track=cybersecurity', label: 'Cybersecurity' },
            { to: '/educenter?track=software-engineering', label: 'Software Engineering' },
            { to: '/dashboard', label: 'My Dashboard' },
          ]} />

          {/* Contact */}
          <div>
            <h4 style={{ 
              fontSize: 13, fontWeight: 700, color: 'var(--ac-white)', 
              marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' 
            }}>Contact</h4>
            {[
              { Icon: Mail, text: 'sithika@alteracode.org' },
              { Icon: MapPin, text: 'Wattala, Sri Lanka' },
            ].map(({ Icon, text }) => (
              <div key={text} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 }}>
                <Icon size={14} color="var(--ac-cyan)" style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'var(--ac-muted)' }}>{text}</span>
              </div>
            ))}

            {/* Scholar launch badge */}
            <div style={{
              marginTop: '1.5rem',
              background: 'rgba(168,85,247,0.08)',
              border: '1px solid rgba(168,85,247,0.2)',
              borderRadius: 'var(--radius-md)',
              padding: '0.75rem 1rem',
            }}>
              <p style={{ fontSize: 11, color: 'var(--ac-purple)', fontWeight: 700, marginBottom: 3 }}>
                🎓 Altera Scholar
              </p>
              <p style={{ fontSize: 11, color: 'var(--ac-muted)', lineHeight: 1.5 }}>
                Launching July 2026 — Video series for school leavers covering all IT pathways
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ 
          borderTop: '1px solid var(--ac-border)', 
          padding: '1.5rem 0', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: '1rem' 
        }}>
          <div>
            <p style={{ fontSize: 12, color: 'var(--ac-muted)' }}>
              © {new Date().getFullYear()} AlteraCode. All rights reserved.
            </p>
            <p style={{ fontSize: 11, color: 'var(--ac-muted)', marginTop: 3 }}>
              Powered by 13+ developers · Building globally 🌍
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map(t => (
              <a key={t} href="#" style={{ fontSize: 12, color: 'var(--ac-muted)', textDecoration: 'none' }}
                onMouseEnter={e => e.target.style.color = 'var(--ac-cyan)'}
                onMouseLeave={e => e.target.style.color = 'var(--ac-muted)'}
              >{t}</a>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          footer .container > div:first-child { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 480px) {
          footer .container > div:first-child { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </footer>
  );
}